import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Avatar,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Stack,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  MenuItem
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import CodeOutlined from '@mui/icons-material/CodeOutlined';
import WorkspacePremiumOutlined from '@mui/icons-material/WorkspacePremiumOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import HourglassEmptyOutlined from '@mui/icons-material/HourglassEmptyOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import { useAuth, api } from '../context/AuthContext';
import { INDIAN_STATES } from '../data/indianStates';
import axios from 'axios';

const emptyProject = {
  projectName: '',
  role: '',
  techStack: '',
  duration: '',
  projectUrl: '',
  description: ''
};

const emptyCertification = {
  certificationName: '',
  issuedBy: '',
  certificationId: '',
  certificationUrl: '',
  validityFrom: '',
  validityTo: '',
  doesNotExpire: false
};

const emptyLanguage = {
  language: '',
  proficiency: 'Proficient',
  canRead: true,
  canWrite: true,
  canSpeak: true
};

const emptyPersonalDetails = {
  gender: '',
  maritalStatus: '',
  dateOfBirth: '',
  address: '',
  workPermit: '',
  category: '',
  passedOutYear: ''
};

const emptyResume = {
  resumeUrl: '',
  resumeFileName: ''
};

const emptyEducation = {
  education: '',
  university: '',
  course: '',
  specialization: '',
  courseType: 'Full time',
  startYear: '',
  endYear: '',
  gradingSystem: '',
  gradingValue: ''
};
const emptyExperience = {
  currentEmployment: 'Yes',
  employmentType: 'Full-time',
  totalExperienceYears: '0',
  totalExperienceMonths: '0',
  currentCompanyName: '',
  currentJobTitle: '',
  joiningYear: '',
  joiningMonth: '',
  currentSalaryCurrency: '₹',
  currentSalary: '',
  skillsUsed: '',
  jobProfile: '',
  noticePeriod: '',
  description: ''
};

function parseMaybeJson(value) {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function safeText(value) {
  if (value == null) return '';
  if (Array.isArray(value)) return '';
  const text = String(value).trim();
  return text === '[]' ? '' : text;
}

function normalizeProjects(value) {
  const parsed = parseMaybeJson(value);

  if (Array.isArray(parsed)) {
    return parsed.map((item, index) => ({
      id: item.id || `${Date.now()}-${index}`,
      projectName: safeText(item.projectName || item.title),
      role: safeText(item.role),
      techStack: safeText(item.techStack),
      duration: safeText(item.duration),
      projectUrl: safeText(item.projectUrl || item.url),
      description: safeText(item.description)
    }));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '[]') return [];
    return [{
      id: `${Date.now()}-legacy`,
      projectName: trimmed,
      role: '',
      techStack: '',
      duration: '',
      projectUrl: '',
      description: trimmed
    }];
  }

  return [];
}

function normalizeCertifications(value) {
  const parsed = parseMaybeJson(value);

  if (Array.isArray(parsed)) {
    return parsed.map((item, index) => ({
      id: item.id || `${Date.now()}-${index}`,
      certificationName: safeText(item.certificationName || item.name),
      issuedBy: safeText(item.issuedBy),
      certificationId: safeText(item.certificationId),
      certificationUrl: safeText(item.certificationUrl || item.url),
      validityFrom: safeText(item.validityFrom),
      validityTo: safeText(item.validityTo),
      doesNotExpire: Boolean(item.doesNotExpire)
    }));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '[]') return [];
    return [{
      id: `${Date.now()}-legacy`,
      certificationName: trimmed,
      issuedBy: '',
      certificationId: '',
      certificationUrl: '',
      validityFrom: '',
      validityTo: '',
      doesNotExpire: true
    }];
  }

  return [];
}

function normalizeLanguages(value) {
  const parsed = parseMaybeJson(value);
  if (Array.isArray(parsed)) {
    return parsed.map((item, index) => ({
      id: item.id || `${Date.now()}-${index}`,
      language: safeText(item.language),
      proficiency: safeText(item.proficiency) || 'Proficient',
      canRead: item.canRead !== false,
      canWrite: item.canWrite !== false,
      canSpeak: item.canSpeak !== false
    }));
  }

  return [];
}

function normalizePersonalDetails(value) {
  const parsed = parseMaybeJson(value);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return {
      gender: safeText(parsed.gender),
      maritalStatus: safeText(parsed.maritalStatus),
      dateOfBirth: safeText(parsed.dateOfBirth),
      address: safeText(parsed.address),
      workPermit: safeText(parsed.workPermit),
      category: safeText(parsed.category),
      passedOutYear: safeText(parsed.passedOutYear)
    };
  }

  return { ...emptyPersonalDetails };
}

function normalizeEducation(value, legacy = {}) {
  const parsed = parseMaybeJson(value);

  if (Array.isArray(parsed)) {
    return parsed.map((item, index) => ({
      id: item.id || `${Date.now()}-${index}`,
      education: safeText(item.education || item.level),
      university: safeText(item.university || item.collegeName || item.schoolName || item.institution),
      course: safeText(item.course || item.qualification || item.degree),
      specialization: safeText(item.specialization),
      courseType: safeText(item.courseType) || 'Full time',
      startYear: safeText(item.startYear || item.passedOutYear || item.year),
      endYear: safeText(item.endYear),
      gradingSystem: safeText(item.gradingSystem || item.percentage || item.cgpa || item.mark),
      gradingValue: safeText(item.gradingValue || item.score || item.value)
    }));
  }

  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return [{
      id: parsed.id || `${Date.now()}-legacy`,
      education: safeText(parsed.education || parsed.level),
      university: safeText(parsed.university || parsed.collegeName || parsed.schoolName || parsed.institution),
      course: safeText(parsed.course || parsed.qualification || parsed.degree),
      specialization: safeText(parsed.specialization),
      courseType: safeText(parsed.courseType) || 'Full time',
      startYear: safeText(parsed.startYear || parsed.passedOutYear || parsed.year),
      endYear: safeText(parsed.endYear),
      gradingSystem: safeText(parsed.gradingSystem || parsed.percentage || parsed.cgpa || parsed.mark)
    }];
  }

  const legacyItem = {
    id: `${Date.now()}-legacy`,
    education: safeText(legacy.education || legacy.level),
    university: safeText(legacy.university || legacy.college_name || legacy.collegeName),
    course: safeText(legacy.course || legacy.qualification),
    specialization: safeText(legacy.specialization),
    courseType: safeText(legacy.courseType) || 'Full time',
    startYear: safeText(legacy.startYear || legacy.passed_out_year || legacy.passedOutYear),
    endYear: safeText(legacy.endYear),
    gradingSystem: safeText(legacy.gradingSystem || legacy.percentage)
  };

  if (
    legacyItem.education ||
    legacyItem.university ||
    legacyItem.course ||
    legacyItem.specialization ||
    legacyItem.startYear ||
    legacyItem.endYear ||
    legacyItem.gradingSystem
  ) {
    return [legacyItem];
  }

  return [];
}

function normalizeExperience(value, legacy = {}) {
  const parsed = parseMaybeJson(value);

  if (Array.isArray(parsed)) {
    return parsed.map((item, index) => ({
      id: item.id || `${Date.now()}-${index}`,
      currentEmployment: safeText(item.currentEmployment),
      employmentType: safeText(item.employmentType),
      totalExperienceYears: safeText(item.totalExperienceYears),
      totalExperienceMonths: safeText(item.totalExperienceMonths),
      currentCompanyName: safeText(item.currentCompanyName),
      currentJobTitle: safeText(item.currentJobTitle),
      joiningYear: safeText(item.joiningYear),
      joiningMonth: safeText(item.joiningMonth),
      currentSalaryCurrency: safeText(item.currentSalaryCurrency) || '₹',
      currentSalary: safeText(item.currentSalary),
      skillsUsed: safeText(item.skillsUsed),
      jobProfile: safeText(item.jobProfile),
      noticePeriod: safeText(item.noticePeriod)
    }));
  }

  const legacyItem = {
    id: `${Date.now()}-legacy`,
    currentEmployment: safeText(legacy.currentEmployment),
    employmentType: safeText(legacy.employmentType),
    totalExperienceYears: safeText(legacy.totalExperienceYears),
    totalExperienceMonths: safeText(legacy.totalExperienceMonths),
    currentCompanyName: safeText(legacy.currentCompanyName || legacy.companyWorked || legacy.company_name),
    currentJobTitle: safeText(legacy.currentJobTitle || legacy.designation || legacy.role),
    joiningYear: safeText(legacy.joiningYear || legacy.startDate),
    joiningMonth: safeText(legacy.joiningMonth),
    currentSalaryCurrency: safeText(legacy.currentSalaryCurrency) || '₹',
    currentSalary: safeText(legacy.currentSalary),
    skillsUsed: safeText(legacy.skillsUsed),
    jobProfile: safeText(legacy.jobProfile || legacy.description),
    noticePeriod: safeText(legacy.noticePeriod)
  };

  if (legacyItem.currentCompanyName || legacyItem.currentJobTitle) {
    return [legacyItem];
  }

  return [];
}

function Profile() {
  const { user, setUser } = useAuth();

 const [formData, setFormData] = useState({
   firstName: '',
   lastName: '',
   phone: '',
   companyName: '',
   companyWebsite: '',
   industry: '',
   companySize: '',
   companyLocation: '',
   stateCode: '',
   companyDescription: '',
   foundedYear: '',
   linkedinCompanyPage: '',
   companyEmail: '',
   recruiterDesignation: '',
   gstNumber: '',
   companyLogoUrl: '',
   hiringLocations: '',
   workModel: '',
   officialContactNumber: '',
   profileImageUrl: '',
   qualification: '',
   collegeName: '',
   passedOutYear: '',
   percentage: '',
   skills: '',
   companyWorked: '',
   experience: '',
   linkedinUrl: '',
   githubUrl: '',
   profileSummary: '',
   resumeUrl: '',
   companyDocumentUrl: '',
   companyDocumentName: '',
   companyDocumentUploadedAt: '',
   gstCertificateUrl: '',
   gstCertificateName: '',
   gstCertificateUploadedAt: '',
   panCardUrl: '',
   panCardName: '',
   panCardUploadedAt: '',
   professionalTaxUrl: '',
   professionalTaxName: '',
   professionalTaxUploadedAt: '',
   epfoRegistrationUrl: '',
   epfoRegistrationName: '',
   epfoRegistrationUploadedAt: ''
 });

  const [resumeFileName, setResumeFileName] = useState('');
  const [basicInfoDraft, setBasicInfoDraft] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [personalDetails, setPersonalDetails] = useState({ ...emptyPersonalDetails });

  const [educationList, setEducationList] = useState([]);
  const [editingEducationIndex, setEditingEducationIndex] = useState(null);

  const [experienceList, setExperienceList] = useState([]);
  const [editingExperienceIndex, setEditingExperienceIndex] = useState(null);

  const [projects, setProjects] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [gstUploading, setGstUploading] = useState(false);
  const [panUploading, setPanUploading] = useState(false);
  const [professionalTaxUploading, setProfessionalTaxUploading] = useState(false);
  const [epfoUploading, setEpfoUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [activeDialog, setActiveDialog] = useState(null);

  const [summaryDraft, setSummaryDraft] = useState('');
  const [personalDraft, setPersonalDraft] = useState({ ...emptyPersonalDetails });
  const [educationDraft, setEducationDraft] = useState({ ...emptyEducation });
  const [skillsDraft, setSkillsDraft] = useState('');
  const [experienceDraft, setExperienceDraft] = useState({ ...emptyExperience });
  const [resumeDraft, setResumeDraft] = useState({ ...emptyResume });
  const [languageDraft, setLanguageDraft] = useState({ ...emptyLanguage });
  const [editingLanguageIndex, setEditingLanguageIndex] = useState(null);

  const [projectDraft, setProjectDraft] = useState({ ...emptyProject });
  const [editingProjectIndex, setEditingProjectIndex] = useState(null);

  const [certDraft, setCertDraft] = useState({ ...emptyCertification });
  const [editingCertIndex, setEditingCertIndex] = useState(null);

  useEffect(() => {
    if (!user) return;

    console.log("PROFILE DOC", {
      url: user.company_verification_document_url,
      name: user.company_document_name
    });

    console.log('[Profile] user loaded — document fields:', {
      company_verification_document_url: user.company_verification_document_url,
      company_document_name: user.company_document_name,
      company_document_uploaded_at: user.company_document_uploaded_at,
      companyVerificationDocumentUrl: user.companyVerificationDocumentUrl,
      companyLicenseUrl: user.companyLicenseUrl,
      gst_certificate_url: user.gst_certificate_url,
      pan_card_url: user.pan_card_url,
      professional_tax_url: user.professional_tax_url,
      epfo_registration_url: user.epfo_registration_url
    });

    setFormData({
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      phone: user.phone || '',
      companyName: user.company_name || '',
      companyWebsite: user.company_website || '',
      industry: user.industry || '',
      companySize: user.company_size || '',
      companyLocation: user.company_location || '',
      stateCode: user.state_code || '',
      companyDescription: user.company_description || '',
      foundedYear: user.founded_year || '',
      linkedinCompanyPage: user.linkedin_company_page || '',
      companyEmail: user.company_email || '',
      recruiterDesignation: user.recruiter_designation || '',
      gstNumber: user.gst_number || '',
      companyLogoUrl: user.company_logo_url || '',
      hiringLocations: user.hiring_locations || '',
      workModel: user.work_model || '',
      officialContactNumber: user.official_contact_number || '',
      profileImageUrl: user.profile_image_url || '',
      qualification: user.qualification || '',
      collegeName: user.college_name || '',
      passedOutYear: user.passed_out_year || '',
      percentage: user.percentage || '',
      skills: user.skills || '',
      companyWorked: user.company_worked || '',
      experience: user.experience || '',
      linkedinUrl: user.linkedin_url || '',
      githubUrl: user.github_url || '',
      profileSummary: user.profile_summary || '',
      resumeUrl: user.resume_url || '',
      companyDocumentUrl: user.company_verification_document_url || user.companyLicenseUrl || '',
      companyDocumentName: user.company_document_name || '',
      companyDocumentUploadedAt: user.company_document_uploaded_at || null,
      gstCertificateUrl: user.gst_certificate_url || '',
      gstCertificateName: user.gst_certificate_name || '',
      gstCertificateUploadedAt: user.gst_certificate_uploaded_at || null,
      panCardUrl: user.pan_card_url || '',
      panCardName: user.pan_card_name || '',
      panCardUploadedAt: user.pan_card_uploaded_at || null,
      professionalTaxUrl: user.professional_tax_url || '',
      professionalTaxName: user.professional_tax_name || '',
      professionalTaxUploadedAt: user.professional_tax_uploaded_at || null,
      epfoRegistrationUrl: user.epfo_registration_url || '',
      epfoRegistrationName: user.epfo_registration_name || '',
      epfoRegistrationUploadedAt: user.epfo_registration_uploaded_at || null
    });

    setResumeFileName(user.resume_file_name || '');
    const normalizedPersonalDetails = normalizePersonalDetails(user.personal_details);
    setPersonalDetails({
      ...normalizedPersonalDetails,
      passedOutYear: normalizedPersonalDetails.passedOutYear || user.passed_out_year || ''
    });
    setEducationList(normalizeEducation(user.education, user));
    setExperienceList(normalizeExperience(user.experience_list || user.experienceList || user.experiences, user));
    setProjects(normalizeProjects(user.projects));
    setCertifications(normalizeCertifications(user.certifications));
    setLanguages(normalizeLanguages(user.languages));
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError('Image size should be less than 2MB');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('profileImage', file);

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'https://api.exihire.com'}/auth/upload-profile-image`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const { url, user: updatedUser } = response.data;

      setFormData(prev => ({
        ...prev,
        profileImageUrl: url
      }));

      if (updatedUser) {
        setUser(updatedUser);
      }

      setSuccess('Profile image updated successfully!');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload profile image');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError('Company logo size should be less than 2MB');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('companyLogo', file);

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'https://api.exihire.com'}/auth/upload-company-logo`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const { url } = response.data;

      setFormData(prev => ({
        ...prev,
        companyLogoUrl: url
      }));

      setSuccess('Company logo updated successfully!');
    } catch (err) {
      console.error('Company logo upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload company logo');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    console.log('[handleCompanyDocumentUpload] selected file:', file.name, file.size);

    if (file.size > 5 * 1024 * 1024) {
      setError('Document size should be less than 5MB');
      return;
    }

    const allowed = /\.(pdf|png|jpg|jpeg)$/i.test(file.name);
    if (!allowed) {
      setError('Only PDF, PNG, JPG, and JPEG files are allowed');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('companyDocument', file);

    setDocumentUploading(true);
    setError('');
    setSuccess('');

    try {
      console.log('[handleCompanyDocumentUpload] starting upload to /auth/company-document');
      const response = await api.put(
        '/auth/company-document',
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const { documentUrl, documentName, documentUploadedAt } = response.data;
      console.log('[handleCompanyDocumentUpload] success:', { documentUrl, documentName, documentUploadedAt });
      console.log('[handleCompanyDocumentUpload] response:', { documentUrl, documentName, documentUploadedAt });

      setFormData(prev => ({
        ...prev,
        companyDocumentUrl: documentUrl,
        companyDocumentName: documentName,
        companyDocumentUploadedAt: documentUploadedAt
      }));

      setUser(prev => ({
        ...prev,
        company_verification_document_url: documentUrl,
        company_document_name: documentName,
        company_document_uploaded_at: documentUploadedAt
      }));

      setSuccess('Company verification document updated successfully!');
    } catch (err) {
      console.error('Document upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload document');
    } finally {
      setDocumentUploading(false);
    }
  };

  const handleGstCertificateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Document size should be less than 5MB'); return; }
    if (!/\.(pdf|png|jpg|jpeg)$/i.test(file.name)) { setError('Only PDF, PNG, JPG, and JPEG files are allowed'); return; }
    const formDataUpload = new FormData();
    formDataUpload.append('gstCertificate', file);
    setGstUploading(true); setError(''); setSuccess('');
    try {
      const response = await api.put(
        '/auth/gst-certificate',
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const { documentUrl, documentName, documentUploadedAt } = response.data;
      setFormData(prev => ({ ...prev, gstCertificateUrl: documentUrl, gstCertificateName: documentName, gstCertificateUploadedAt: documentUploadedAt }));
      setUser(prev => ({ ...prev, gst_certificate_url: documentUrl, gst_certificate_name: documentName, gst_certificate_uploaded_at: documentUploadedAt }));
      setSuccess('GST certificate uploaded successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload GST certificate');
    } finally { setGstUploading(false); }
  };

  const handlePanCardUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Document size should be less than 5MB'); return; }
    if (!/\.(pdf|png|jpg|jpeg)$/i.test(file.name)) { setError('Only PDF, PNG, JPG, and JPEG files are allowed'); return; }
    const formDataUpload = new FormData();
    formDataUpload.append('panCard', file);
    setPanUploading(true); setError(''); setSuccess('');
    try {
      const response = await api.put(
        '/auth/pan-card',
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const { documentUrl, documentName, documentUploadedAt } = response.data;
      setFormData(prev => ({ ...prev, panCardUrl: documentUrl, panCardName: documentName, panCardUploadedAt: documentUploadedAt }));
      setUser(prev => ({ ...prev, pan_card_url: documentUrl, pan_card_name: documentName, pan_card_uploaded_at: documentUploadedAt }));
      setSuccess('PAN card uploaded successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload PAN card');
    } finally { setPanUploading(false); }
  };

  const handleProfessionalTaxUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Document size should be less than 5MB'); return; }
    if (!/\.(pdf|png|jpg|jpeg)$/i.test(file.name)) { setError('Only PDF, PNG, JPG, and JPEG files are allowed'); return; }
    const formDataUpload = new FormData();
    formDataUpload.append('professionalTax', file);
    setProfessionalTaxUploading(true); setError(''); setSuccess('');
    try {
      const response = await api.put(
        '/auth/professional-tax',
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const { documentUrl, documentName, documentUploadedAt } = response.data;
      setFormData(prev => ({ ...prev, professionalTaxUrl: documentUrl, professionalTaxName: documentName, professionalTaxUploadedAt: documentUploadedAt }));
      setUser(prev => ({ ...prev, professional_tax_url: documentUrl, professional_tax_name: documentName, professional_tax_uploaded_at: documentUploadedAt }));
      setSuccess('Professional Tax Registration uploaded successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload Professional Tax Registration');
    } finally { setProfessionalTaxUploading(false); }
  };

  const handleDownloadDocument = async (docUrl, docName) => {
    if (!docUrl) {
      setError('Document not found.');
      return;
    }
    console.log('[handleDownloadDocument] url:', docUrl, 'name:', docName);

    // Open a blank tab immediately (sync, in the click event) to avoid popup blockers
    const newTab = window.open('', '_blank');

    try {
      // Extract S3 key from the stored URL: https://bucket.s3.region.amazonaws.com/KEY
      let s3Key = '';
      try {
        s3Key = new URL(docUrl).pathname.replace(/^\//, '');
      } catch (_) {
        s3Key = docUrl.split('.amazonaws.com/').slice(1).join('.amazonaws.com/');
      }
      console.log('[handleDownloadDocument] extracted key:', s3Key);

      const response = await api.get(
        '/auth/download-document',
        {
          params: { key: s3Key, filename: docName || 'document' }
        }
      );

      const { downloadUrl } = response.data;
      console.log('[handleDownloadDocument] presigned URL received, redirecting tab');

      // Redirect the already-open tab to the presigned URL
      // S3 responds with Content-Disposition: attachment — browser downloads the file
      if (newTab) {
        newTab.location.href = downloadUrl;
      } else {
        window.location.href = downloadUrl;
      }
    } catch (err) {
      console.error('[handleDownloadDocument] error:', err.response?.data || err.message);
      if (newTab) newTab.close();
      if (err.response?.status === 404) {
        setError('Document not found.');
      } else if (err.response?.status === 403) {
        setError('Access denied.');
      } else {
        setError('Unable to download document. Please try again.');
      }
    }
  };

  const handleEpfoRegistrationUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Document size should be less than 5MB'); return; }
    if (!/\.(pdf|png|jpg|jpeg)$/i.test(file.name)) { setError('Only PDF, PNG, JPG, and JPEG files are allowed'); return; }
    const formDataUpload = new FormData();
    formDataUpload.append('epfoRegistration', file);
    setEpfoUploading(true); setError(''); setSuccess('');
    try {
      const response = await api.put(
        '/auth/epfo-registration',
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const { documentUrl, documentName, documentUploadedAt } = response.data;
      setFormData(prev => ({ ...prev, epfoRegistrationUrl: documentUrl, epfoRegistrationName: documentName, epfoRegistrationUploadedAt: documentUploadedAt }));
      setUser(prev => ({ ...prev, epfo_registration_url: documentUrl, epfo_registration_name: documentName, epfo_registration_uploaded_at: documentUploadedAt }));
      setSuccess('EPFO Registration uploaded successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload EPFO Registration');
    } finally { setEpfoUploading(false); }
  };

  const handleResumeFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError('Resume size should be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setResumeDraft({
        resumeUrl: String(reader.result || ''),
        resumeFileName: file.name
      });
      setSuccess('Resume file selected. Click Save Changes to store it.');
    };
    reader.onerror = () => {
      setError('Failed to read the resume file');
    };
    reader.readAsDataURL(file);
  };

  const handlePreviewResume = (url) => {
    const previewUrl = String(url || '').trim();

    if (!previewUrl) {
      setError('Please upload a resume first');
      return;
    }

    const previewWindow = window.open(previewUrl, '_blank', 'noopener,noreferrer');
    if (previewWindow) {
      previewWindow.opener = null;
    }
  };
  const handleDownloadResume = (url) => {
    const downloadUrl = String(url || '').trim();
    if (!downloadUrl) {
      setError('Please upload a resume first');
      return;
    }

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = resumeFileName || 'resume';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteResume = () => {
    setFormData(prev => ({
      ...prev,
      resumeUrl: ''
    }));
    setResumeFileName('');
    setResumeDraft({ resumeUrl: '', resumeFileName: '' });
    setSuccess('Resume removed. Click Save Changes to keep it removed.');
  };


  const openDialog = (type) => {
    setError('');
    setSuccess('');

    if (type === 'basic-info') {
      setBasicInfoDraft({
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        phone: formData.phone || ''
      });
    }

    if (type === 'summary') {
      setSummaryDraft(formData.profileSummary || '');
    }
    if (type === 'personal') {
      setPersonalDraft(personalDetails || { ...emptyPersonalDetails });
    }
    if (type === 'education') {
      setEducationDraft({ ...emptyEducation });
      setEditingEducationIndex(null);
    }
    if (type === 'skills') {
      setSkillsDraft(formData.skills || '');
    }
    if (type === 'experience') {
      setExperienceDraft({ ...emptyExperience });
      setEditingExperienceIndex(null);
    }
    if (type === 'resume') {
      setResumeDraft({
        resumeUrl: formData.resumeUrl || '',
        resumeFileName: resumeFileName || ''
      });
    }
    if (type === 'language') {
      setLanguageDraft({ ...emptyLanguage });
      setEditingLanguageIndex(null);
    }
    if (type === 'project') {
      setProjectDraft({ ...emptyProject });
      setEditingProjectIndex(null);
    }
    if (type === 'certification') {
      setCertDraft({ ...emptyCertification });
      setEditingCertIndex(null);
    }

    setActiveDialog(type);
  };

  const closeDialog = () => {
    setActiveDialog(null);
    setEditingEducationIndex(null);
    setEditingExperienceIndex(null);
    setEditingLanguageIndex(null);
    setEditingProjectIndex(null);
    setEditingCertIndex(null);
  };

  const saveActiveDialog = () => {
    if (activeDialog === 'basic-info') {
      setFormData(prev => ({
        ...prev,
        firstName: basicInfoDraft.firstName,
        lastName: basicInfoDraft.lastName,
        phone: basicInfoDraft.phone
      }));
      closeDialog();
      return;
    }

    if (activeDialog === 'summary') {
      setFormData(prev => ({ ...prev, profileSummary: summaryDraft }));
      closeDialog();
      return;
    }

    if (activeDialog === 'personal') {
      setPersonalDetails(personalDraft);
      setFormData(prev => ({
        ...prev,
        passedOutYear: personalDraft.passedOutYear
      }));
      closeDialog();
      return;
    }

    if (activeDialog === 'education') {
      if (!educationDraft.education.trim() && !educationDraft.course.trim()) {
        setError('Education details are required');
        return;
      }

      setEducationList(prev => {
        const next = [...prev];
        const item = {
          id: editingEducationIndex !== null ? next[editingEducationIndex]?.id : Date.now().toString(),
          ...educationDraft
        };

        if (editingEducationIndex !== null) {
          next[editingEducationIndex] = item;
        } else {
          next.push(item);
        }
        return next;
      });

      closeDialog();
      return;
    }

    if (activeDialog === 'skills') {
      setFormData(prev => ({ ...prev, skills: skillsDraft }));
      closeDialog();
      return;
    }

    if (activeDialog === 'experience') {
      setExperienceList(prev => {
        const next = [...prev];
        const item = {
          id: editingExperienceIndex !== null ? next[editingExperienceIndex]?.id : Date.now().toString(),
          ...experienceDraft
        };

        if (editingExperienceIndex !== null) {
          next[editingExperienceIndex] = item;
        } else {
          next.push(item);
        }
        return next;
      });

      closeDialog();
      return;
    }

    if (activeDialog === 'resume') {
      setFormData(prev => ({
        ...prev,
        resumeUrl: resumeDraft.resumeUrl
      }));
      setResumeFileName(resumeDraft.resumeFileName || resumeFileName);
      closeDialog();
      return;
    }

    if (activeDialog === 'language') {
      if (!languageDraft.language.trim()) {
        setError('Language is required');
        return;
      }

      setLanguages(prev => {
        const next = [...prev];
        const item = {
          id: editingLanguageIndex !== null ? next[editingLanguageIndex]?.id : Date.now().toString(),
          ...languageDraft
        };

        if (editingLanguageIndex !== null) {
          next[editingLanguageIndex] = item;
        } else {
          next.push(item);
        }
        return next;
      });

      closeDialog();
      return;
    }

    if (activeDialog === 'project') {
      if (!projectDraft.projectName.trim()) {
        setError('Project name is required');
        return;
      }

      setProjects(prev => {
        const next = [...prev];
        const item = {
          id: editingProjectIndex !== null ? next[editingProjectIndex]?.id : Date.now().toString(),
          ...projectDraft
        };

        if (editingProjectIndex !== null) {
          next[editingProjectIndex] = item;
        } else {
          next.push(item);
        }
        return next;
      });

      closeDialog();
      return;
    }

    if (activeDialog === 'certification') {
      if (!certDraft.certificationName.trim()) {
        setError('Certification name is required');
        return;
      }

      setCertifications(prev => {
        const next = [...prev];
        const item = {
          id: editingCertIndex !== null ? next[editingCertIndex]?.id : Date.now().toString(),
          ...certDraft
        };

        if (editingCertIndex !== null) {
          next[editingCertIndex] = item;
        } else {
          next.push(item);
        }
        return next;
      });

      closeDialog();
    }
  };

  const editEducation = (index) => {
    const item = educationList[index];
    setEducationDraft({
      education: item.education || '',
      university: item.university || '',
      course: item.course || '',
      specialization: item.specialization || '',
      courseType: item.courseType || 'Full time',
      startYear: item.startYear || '',
      endYear: item.endYear || '',
      gradingSystem: item.gradingSystem || '',
      gradingValue: item.gradingValue || ''
    });
    setEditingEducationIndex(index);
    setActiveDialog('education');
  };

  const deleteEducation = (index) => {
    setEducationList(prev => prev.filter((_, i) => i !== index));
  };

  const editExperience = (index) => {
    const item = experienceList[index];
    setExperienceDraft({
      currentEmployment: item.currentEmployment || 'Yes',
      employmentType: item.employmentType || 'Full-time',
      totalExperienceYears: item.totalExperienceYears || '0',
      totalExperienceMonths: item.totalExperienceMonths || '0',
      currentCompanyName: item.currentCompanyName || '',
      currentJobTitle: item.currentJobTitle || '',
      joiningYear: item.joiningYear || '',
      joiningMonth: item.joiningMonth || '',
      currentSalaryCurrency: item.currentSalaryCurrency || '₹',
      currentSalary: item.currentSalary || '',
      skillsUsed: item.skillsUsed || '',
      jobProfile: item.jobProfile || '',
      noticePeriod: item.noticePeriod || '',
      description: item.description || ''
    });
    setEditingExperienceIndex(index);
    setActiveDialog('experience');
  };

  const deleteExperience = (index) => {
    setExperienceList(prev => prev.filter((_, i) => i !== index));
  };

  const editLanguage = (index) => {
    const item = languages[index];
    setLanguageDraft({
      language: item.language || '',
      proficiency: item.proficiency || 'Proficient',
      canRead: item.canRead !== false,
      canWrite: item.canWrite !== false,
      canSpeak: item.canSpeak !== false
    });
    setEditingLanguageIndex(index);
    setActiveDialog('language');
  };

  const deleteLanguage = (index) => {
    setLanguages(prev => prev.filter((_, i) => i !== index));
  };

  const editProject = (index) => {
    const item = projects[index];
    setProjectDraft({
      projectName: item.projectName || '',
      role: item.role || '',
      techStack: item.techStack || '',
      duration: item.duration || '',
      projectUrl: item.projectUrl || '',
      description: item.description || ''
    });
    setEditingProjectIndex(index);
    setActiveDialog('project');
  };

  const deleteProject = (index) => {
    setProjects(prev => prev.filter((_, i) => i !== index));
  };

  const editCertification = (index) => {
    const item = certifications[index];
    setCertDraft({
      certificationName: item.certificationName || '',
      issuedBy: item.issuedBy || '',
      certificationId: item.certificationId || '',
      certificationUrl: item.certificationUrl || '',
      validityFrom: item.validityFrom || '',
      validityTo: item.validityTo || '',
      doesNotExpire: Boolean(item.doesNotExpire)
    });
    setEditingCertIndex(index);
    setActiveDialog('certification');
  };

  const deleteCertification = (index) => {
    setCertifications(prev => prev.filter((_, i) => i !== index));
  };

  const profileCompletion = useMemo(() => {
    if (!user) return 0;

    const isFilled = (value) => String(value || '').trim().length > 0;
    const scoreSection = (values, weight) => {
      if (!values.length) return 0;
      const filled = values.filter(isFilled).length;
      return Math.round((filled / values.length) * weight);
    };

    if (user?.role === 'super_admin') {
      let score = 0;
      score += scoreSection(
        [formData.firstName, formData.lastName, formData.phone],
        100
      );
      return Math.min(100, score);
    }

    if (user?.role === 'admin') {
      let score = 0;

      score += scoreSection(
        [formData.firstName, formData.lastName, formData.phone],
        10
      );

      score += scoreSection(
        [
          formData.companyName,
          formData.companyWebsite,
          formData.industry,
          formData.companySize,
          formData.companyLocation,
          formData.companyDescription,
          formData.foundedYear
        ],
        25
      );

      score += scoreSection(
        [
          formData.companyDocumentUrl,
          formData.gstCertificateUrl,
          formData.panCardUrl,
          formData.professionalTaxUrl
        ],
        20
      );

      score += scoreSection(
        [
          formData.companyEmail,
          formData.recruiterDesignation,
          formData.officialContactNumber
        ],
        20
      );

      score += scoreSection(
        [formData.hiringLocations, formData.workModel],
        15
      );

      score += scoreSection(
        [formData.linkedinCompanyPage],
        10
      );

      return Math.min(100, score);
    }

    let score = 0;

    score += scoreSection(
      [formData.firstName, formData.lastName, formData.phone],
      15
    );


    score += scoreSection(
      [formData.profileSummary],
      10
    );

    score += scoreSection(
      [
        personalDetails.gender,
        personalDetails.maritalStatus,
        personalDetails.dateOfBirth,
        personalDetails.address,
        personalDetails.workPermit,
        personalDetails.category,
        personalDetails.passedOutYear
      ],
      15
    );

    if (educationList.length > 0) {
      const edu = educationList[0];
      score += scoreSection(
        [
          edu.education,
          edu.university,
          edu.course,
          edu.specialization,
          edu.courseType,
          edu.startYear,
          edu.endYear,
          edu.gradingSystem,
          edu.gradingValue
        ],
        15
      );
    }

    score += scoreSection([formData.skills], 10);

    if (experienceList.length > 0) {
      const exp = experienceList[0];
      score += scoreSection(
        [
          exp.currentEmployment,
          exp.employmentType,
          exp.totalExperienceYears,
          exp.totalExperienceMonths,
          exp.currentCompanyName,
          exp.currentJobTitle,
          exp.joiningYear,
          exp.joiningMonth,
          exp.currentSalary,
          exp.skillsUsed,
          exp.jobProfile,
          exp.noticePeriod
        ],
        15
      );
    }

    if (projects.length > 0) {
      const p = projects[0];
      score += scoreSection(
        [p.projectName, p.role, p.techStack, p.duration, p.description],
        10
      );
    }

    if (certifications.length > 0) {
      const c = certifications[0];
      score += scoreSection(
        [c.certificationName, c.issuedBy, c.certificationId],
        10
      );
    }

    score += scoreSection(
      [formData.linkedinUrl, formData.githubUrl],
      10
    );

    return Math.min(100, score);
  }, [
    user,
    formData,
    resumeFileName,
    personalDetails,
    educationList,
    experienceList,
    projects,
    certifications
  ]);

  const displayName =
    `${formData.firstName || user?.first_name || ''} ${formData.lastName || user?.last_name || ''}`.trim() || 'Profile';

  const profileTitle =
    user?.role === 'admin'
      ? (formData.companyName || 'Recruiter Profile')
      : user?.role === 'super_admin'
        ? 'Super Admin Profile'
        : '';

  const profileSubtitle =
    user?.role === 'admin'
      ? 'Manage your recruiter account details'
      : user?.role === 'super_admin'
        ? 'System Administration & Oversight'
        : 'Build a strong candidate profile';

  const profileInitials =
    `${(formData.firstName || user?.first_name || '')[0] || ''}${(formData.lastName || user?.last_name || '')[0] || ''}`.toUpperCase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    if (
      user?.role === 'admin' &&
      !String(formData.gstNumber || '').trim()
    ) {
      setError('GST / Registration Number is required');
      setLoading(false);
      return;
    }

    try {
      // Sanitize nested objects before stringifying
      const sanitizedPersonalDetails = { 
        ...personalDetails,
        dateOfBirth: personalDetails.dateOfBirth === '' ? null : personalDetails.dateOfBirth,
        passedOutYear: personalDetails.passedOutYear === '' ? null : personalDetails.passedOutYear
      };
      
      const sanitizedEducation = educationList.map(edu => ({
        ...edu,
        startYear: edu.startYear === '' ? null : edu.startYear,
        endYear: edu.endYear === '' ? null : edu.endYear
      }));
      
      const sanitizedExperience = experienceList.map(exp => ({
        ...exp,
        joiningYear: exp.joiningYear === '' ? null : exp.joiningYear,
        joiningMonth: exp.joiningMonth === '' ? null : exp.joiningMonth,
        lastWorkingDay: exp.lastWorkingDay === '' ? null : exp.lastWorkingDay
      }));

      const sanitizedCertifications = certifications.map(cert => ({
        ...cert,
        validityFrom: cert.validityFrom === '' ? null : cert.validityFrom,
        validityTo: cert.validityTo === '' ? null : cert.validityTo
      }));

      let payload = {};

      if (user?.role === 'super_admin') {
        payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone
        };
      } else {
        payload = {
          ...formData,
          companyName: formData.companyName,
          companyWebsite: formData.companyWebsite,
          industry: formData.industry,
          companySize: formData.companySize,
          companyLocation: formData.companyLocation,
          companyDescription: formData.companyDescription,
          linkedinCompanyPage: formData.linkedinCompanyPage,
          companyEmail: formData.companyEmail,
          recruiterDesignation: formData.recruiterDesignation,
          gstNumber: formData.gstNumber || null,
          companyLogoUrl: formData.companyLogoUrl || null,
          hiringLocations: formData.hiringLocations,
          workModel: formData.workModel,
          officialContactNumber: formData.officialContactNumber,
          resumeUrl: formData.resumeUrl || null,
          resumeFileName: resumeFileName || null,
          personalDetails: JSON.stringify(sanitizedPersonalDetails),
          education: JSON.stringify(sanitizedEducation),
          educationList: JSON.stringify(sanitizedEducation),
          experienceList: JSON.stringify(sanitizedExperience),
          experience_list: JSON.stringify(sanitizedExperience),
          languages: JSON.stringify(languages),
          projects: JSON.stringify(projects),
          certifications: JSON.stringify(sanitizedCertifications)
        };
      }

      // Convert empty strings to null for timestamp/date fields
      const timestampFields = [
        'companyDocumentUploadedAt',
        'gstCertificateUploadedAt',
        'panCardUploadedAt',
        'professionalTaxUploadedAt',
        'epfoRegistrationUploadedAt'
      ];

      timestampFields.forEach(field => {
        if (payload[field] === '') {
          payload[field] = null;
        }
      });

      console.log('[handleSubmit] Sanity check - timestamp fields:', timestampFields.map(f => `${f}: ${payload[f]}`).join(', '));

      const foundedYearText = String(formData.foundedYear || '').trim();
      if (foundedYearText) {
        payload.foundedYear = Number(foundedYearText);
      } else {
        delete payload.foundedYear;
      }

      console.log('[handleSubmit] starting profile update with payload:', payload);
      const response = await api.patch(
        '/auth/profile',
        payload
      );
      console.log('[handleSubmit] patch success, response user:', response.data?.user);

      const userData = response.data?.user || response.data;

      if (userData && (userData.id || userData.email)) {
        setUser(userData);
        setSuccess('Profile updated successfully!');
      } else {
        setError('Update successful, but failed to sync profile data.');
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  const INDIGO = "#1E3A8A";

  const sectionButtonSx = {
    justifyContent: 'flex-start',
    textTransform: 'none',
    borderRadius: 2,
    px: 1.5,
    py: 1,
    color: INDIGO,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: '#ecfeff'
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#fff'
    }
  };



  const sectionCardSx = {
    p: 2.5,
    borderRadius: 0,
    border: "1px solid transparent",
    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
    background: "#fff",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover, &:focus-within": {
      borderColor: INDIGO,
      boxShadow: "0 8px 20px rgba(30,58,138,0.12)",
    },
  };

  const renderCandidateCardHeader = (title, subtitle, onEdit, extraAction) => (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
      <Box>
        <Typography
            sx={{
              fontSize: "1.35rem",   // Same size as "Quick sections"
              fontWeight: 800,
              color: INDIGO,
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              mb: 0.3
            }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
             fontSize: "0.95rem", // 15px
             fontWeight: 400,
             lineHeight: 1.5,
             color: "#64748B"
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        {extraAction}
        {onEdit && (
          <IconButton onClick={onEdit} size="small" sx={{ bgcolor: '#F8FAFC' }}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F8FAFC 0%, #ffffff 100%)', py: 4 }}>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Profile
                </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

        <Paper
          sx={{
            ...sectionCardSx,
            p: { xs: 2.5, md: 3 },
            mb: 3,
            overflow: "hidden",
            background: "linear-gradient(135deg, #ffffff 0%, #f7ffff 100%)",
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  sx={{
                    width: 160,
                    height: 160,
                    mx: 'auto',
                    mb: 2,
                    border: '6px solid #d9f7f7',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                    fontSize: 42,
                    fontWeight: 700,
                    bgcolor: '#1E3A8A'
                  }}
                  src={
                    formData.profileImageUrl
                      ? `${formData.profileImageUrl}?t=${Date.now()}`
                      : (user?.profile_image_url ? `${user.profile_image_url}?t=${Date.now()}` : null)
                  }
                >
                  {profileInitials}
                </Avatar>

                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="profile-image-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="profile-image-upload">
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="span"
                    sx={{
                      position: 'absolute',
                      bottom: 14,
                      right: 10,
                      backgroundColor: '#ffffff',
                      border: '1px solid #c7f1f1',
                      boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <PhotoCamera />
                  </IconButton>
                </label>
              </Box>

              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`${profileCompletion}% Profile Complete`}
                  sx={{
                    fontWeight: 700,
                    bgcolor: INDIGO,
                    color: "#fff",
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              <Stack spacing={1.2}>
                <Box>
                  <Typography
                      sx={{
                        fontSize: "2rem",
                        fontWeight: 800,
                        color: "#111827",
                        lineHeight: 1.2,
                      }}
                  >
                    {displayName || 'Your Name'}
                  </Typography>
                  {profileTitle && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ color: INDIGO, fontWeight: 700 }}>
                        {profileTitle}
                      </Typography>
                      {(user?.role === 'admin' || user?.role === 'recruiter') && (
                        user?.company_verification_status?.toUpperCase() === 'APPROVED' ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              ml: 1,
                            }}
                          >
                            <VerifiedIcon
                              sx={{
                                color: "#2563EB",
                                fontSize: 20,
                                mr: 0.3,
                              }}
                            />

                            <Typography
                              component="span"
                              sx={{
                                color: "#2563EB",
                                fontSize: "15px",
                                fontWeight: 600,
                                lineHeight: 1,
                              }}
                            >
                              Verified
                            </Typography>
                          </Box>
                        ) : user?.company_verification_status?.toUpperCase() === 'REJECTED' ? (
                          <Chip
                            icon={<CancelOutlined />}
                            label="Rejected"
                            size="small"
                            sx={{
                              bgcolor: '#fee2e2',
                              color: '#dc2626',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              '& .MuiChip-icon': { color: '#dc2626', fontSize: '1rem' }
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<HourglassEmptyOutlined />}
                            label="Pending"
                            size="small"
                            sx={{
                              bgcolor: '#fef3c7',
                              color: '#d97706',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              '& .MuiChip-icon': { color: '#d97706', fontSize: '1rem' }
                            }}
                          />
                        )
                      )}
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                    {profileSubtitle}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<AccountCircleOutlined sx={{ color: INDIGO }} />}
                    label={user?.email || 'Email'}
                    variant="outlined"
                  />
                  <Chip
                    icon={<BadgeOutlined sx={{ color: INDIGO }} />}
                    label={user?.role === 'admin' ? 'Recruiter' : user?.role === 'super_admin' ? 'Super Admin' : 'Candidate'}
                    variant="outlined"
                  />
                  {formData.phone && <Chip label={formData.phone} variant="outlined" />}

                </Stack>

                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={profileCompletion}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      bgcolor: '#e5f8f8',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        bgcolor: '#1E3A8A'
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: '#6b7280' }}>
                    Add more details to improve your profile strength and visibility.
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={3}>
            <Paper

                sx={{
                  ...sectionCardSx,
                  p: 2,
                  position: { md: "sticky" },
                  top: { md: 100 },
                  backgroundColor: "#fff",
                }}
              >
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 1.5, color: INDIGO }}>
                Quick sections
              </Typography>
              <Stack spacing={0.7}>
                <Button sx={sectionButtonSx} onClick={() => document.getElementById('basic-info')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                  <AccountCircleOutlined sx={{ mr: 1, fontSize: 20 }} />
                  Basic info
                </Button>

                {user?.role === 'super_admin' ? null : user?.role !== 'admin' ? (
                  <>

                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('summary')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <EditOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
                      Profile summary
                    </Button>
                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('personal')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <PersonOutlineOutlined sx={{ mr: 1, fontSize: 20 }} />
                      Personal details
                    </Button>
                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('education')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <SchoolOutlined sx={{ mr: 1, fontSize: 20 }} />
                      Education
                    </Button>
                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('languages')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <LanguageOutlined sx={{ mr: 1, fontSize: 20 }} />
                      Languages
                    </Button>
                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <CodeOutlined sx={{ mr: 1, fontSize: 20 }} />
                      Skills
                    </Button>
                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <WorkOutlineOutlined sx={{ mr: 1, fontSize: 20 }} />
                      Experience
                    </Button>
                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <WorkspacePremiumOutlined sx={{ mr: 1, fontSize: 20 }} />
                      Projects
                    </Button>
                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('certifications')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <WorkspacePremiumOutlined sx={{ mr: 1, fontSize: 20 }} />
                      Certifications
                    </Button>
                    <Button sx={sectionButtonSx} onClick={() => document.getElementById('links')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      <LinkedInIcon sx={{ mr: 1, fontSize: 20 }} />
                      Links
                    </Button>
                  </>
                ) : (
                  <Button sx={sectionButtonSx} onClick={() => document.getElementById('company')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                    <BadgeOutlined sx={{ mr: 1, fontSize: 20 }} />
                    Company info
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            {user?.role === 'super_admin' ? (
              <Paper
                sx={{
                  ...sectionCardSx,
                  p: { xs: 2.5, md: 4 },
                  backgroundColor: "#fff",
                }}
              >
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Box id="basic-info" sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: INDIGO }}>
                      Super Admin Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} sx={inputSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} sx={inputSx} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Email Address" value={user?.email || ''} disabled sx={inputSx} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Phone Number" name="phone" value={formData.phone} onChange={handleChange} sx={inputSx} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      sx={{
                        minWidth: 180,
                        px: 3,
                        py: 1.2,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 16,
                        boxShadow: '0 10px 20px rgba(30,58,138,0.22)',
                        background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)' }
                      }}
                    >
                      {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ) : user?.role === 'admin' ? (
              <Paper
                sx={{
                  ...sectionCardSx,
                  p: { xs: 2.5, md: 4 },
                  backgroundColor: "#fff",
                }}
              >
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Box id="basic-info" sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontSize: "22px",
                        fontWeight: 800,
                        color: INDIGO,
                        lineHeight: 1.2,
                        letterSpacing: "-0.3px",
                        mb: 2
                      }}
                    >
                      Basic Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} sx={inputSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} sx={inputSx} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Email Address" value={user?.email || ''} disabled sx={inputSx} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Phone Number" name="phone" value={formData.phone} onChange={handleChange} sx={inputSx} />
                      </Grid>

                    </Grid>
                  </Box>

                  <Box id="company" sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontSize: "22px",
                        fontWeight: 800,
                        color: INDIGO,
                        lineHeight: 1.2,
                        letterSpacing: "-0.3px",
                        mb: 2
                      }}
                    >
                      Company Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Company Name"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Company Website"
                          name="companyWebsite"
                          value={formData.companyWebsite}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          select
                          label="Company Size"
                          name="companySize"
                          value={formData.companySize}
                          onChange={handleChange}
                          sx={inputSx}
                        >
                          <MenuItem value="1-10">1-10</MenuItem>
                          <MenuItem value="11-50">11-50</MenuItem>
                          <MenuItem value="51-200">51-200</MenuItem>
                          <MenuItem value="201-500">201-500</MenuItem>
                          <MenuItem value="500+">500+</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Company Location"
                          name="companyLocation"
                          value={formData.companyLocation}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="State"
                          name="stateCode"
                          value={formData.stateCode}
                          onChange={handleChange}
                          helperText="Used to calculate GST on paid plans"
                          sx={inputSx}
                        >
                          {INDIAN_STATES.map(s => (
                            <MenuItem key={s.code} value={s.code}>
                              {s.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Founded Year"
                          name="foundedYear"
                          value={formData.foundedYear}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(prev => ({ ...prev, foundedYear: value }));
                          }}
                          inputProps={{
                            min: 1800,
                            max: new Date().getFullYear(),
                            step: 1
                          }}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required
                          multiline
                          minRows={3}
                          label="Company Description"
                          name="companyDescription"
                          value={formData.companyDescription}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Company Email"
                          name="companyEmail"
                          value={formData.companyEmail}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Recruiter Designation"
                          name="recruiterDesignation"
                          value={formData.recruiterDesignation}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                           required
                          label="GST / Registration Number"
                          name="gstNumber"
                          value={formData.gstNumber}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Official Contact Number"
                          name="officialContactNumber"
                          value={formData.officialContactNumber}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="LinkedIn Company Page"
                          name="linkedinCompanyPage"
                          value={formData.linkedinCompanyPage}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          p: 2.5,
                          backgroundColor: '#FAFBFF'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionOutlined sx={{ fontSize: 18, color: '#1E3A8A' }} />
                            Company Verification Documents
                          </Typography>

                          {/* Helper to render each document row */}
                          {[
                            {
                              label: 'Company License / Registration / Identification Document',
                              required: true,
                              url: formData.companyDocumentUrl,
                              name: formData.companyDocumentName,
                              uploadedAt: formData.companyDocumentUploadedAt,
                              uploading: documentUploading,
                              onChange: handleCompanyDocumentUpload
                            },
                            {
                              label: 'GST Registration Certificate',
                              required: true,
                              url: formData.gstCertificateUrl,
                              name: formData.gstCertificateName,
                              uploadedAt: formData.gstCertificateUploadedAt,
                              uploading: gstUploading,
                              onChange: handleGstCertificateUpload
                            },
                            {
                              label: 'PAN Card of Company',
                              required: true,
                              url: formData.panCardUrl,
                              name: formData.panCardName,
                              uploadedAt: formData.panCardUploadedAt,
                              uploading: panUploading,
                              onChange: handlePanCardUpload
                            },
                            {
                              label: 'Professional Tax Registration',
                              required: true,
                              url: formData.professionalTaxUrl,
                              name: formData.professionalTaxName,
                              uploadedAt: formData.professionalTaxUploadedAt,
                              uploading: professionalTaxUploading,
                              onChange: handleProfessionalTaxUpload
                            },
                            {
                              label: 'EPFO Registration',
                              required: false,
                              url: formData.epfoRegistrationUrl,
                              name: formData.epfoRegistrationName,
                              uploadedAt: formData.epfoRegistrationUploadedAt,
                              uploading: epfoUploading,
                              onChange: handleEpfoRegistrationUpload
                            }
                          ].map((doc) => (
                            <Box key={doc.label} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { mb: 0, pb: 0, borderBottom: 'none' } }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1e293b' }}>
                                {doc.label}{doc.required ? ' *' : ' '}
                                {!doc.required && (
                                  <Typography component="span" variant="caption" sx={{ color: '#64748b', fontWeight: 400, ml: 0.5 }}>
                                    (Optional)
                                  </Typography>
                                )}
                              </Typography>
                              {doc.url ? (
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                    <DescriptionOutlined sx={{ fontSize: 15, color: '#64748b' }} />
                                    <Typography variant="body2" sx={{ color: '#334155' }}>
                                      {doc.name || 'Document'}
                                    </Typography>
                                    {doc.uploadedAt && (
                                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                        Uploaded: {new Date(doc.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                      onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
                                      sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem' }}
                                    >
                                      View
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleDownloadDocument(doc.url, doc.name)}
                                      sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem' }}
                                    >
                                      Download
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      component="label"
                                      disabled={doc.uploading}
                                      sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', borderColor: '#f59e0b', color: '#d97706', '&:hover': { borderColor: '#d97706', backgroundColor: 'rgba(245,158,11,0.05)' } }}
                                    >
                                      {doc.uploading && <CircularProgress size={13} sx={{ mr: 0.5, color: '#d97706' }} />}
                                      Replace
                                      <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg" onChange={doc.onChange} />
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                    Not uploaded
                                  </Typography>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    component="label"
                                    disabled={doc.uploading}
                                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem' }}
                                  >
                                    {doc.uploading && <CircularProgress size={13} sx={{ mr: 0.5 }} />}
                                    Upload Document
                                    <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg" onChange={doc.onChange} />
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          select
                          label="Work Model"
                          name="workModel"
                          value={formData.workModel}
                          onChange={handleChange}
                          sx={inputSx}
                        >
                          <MenuItem value="Onsite">Onsite</MenuItem>
                          <MenuItem value="Remote">Remote</MenuItem>
                          <MenuItem value="Hybrid">Hybrid</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required
                          label="Hiring Locations"
                          name="hiringLocations"
                          value={formData.hiringLocations}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </Grid>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={loading}
                          sx={{
                            minWidth: 180,
                            px: 3,
                            py: 1.2,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 16,
                            boxShadow: '0 10px 20px rgba(30,58,138,0.22)',
                            background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)' }
                          }}
                        >
                          {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Save Changes'}
                        </Button>
                      </Box>

                    </Grid>
                  </Box>

                            </Box>
                          </Paper>
                        ) : (
                          <Paper
                            sx={{
                              ...sectionCardSx,
                              p: { xs: 2.5, md: 4 },
                              backgroundColor: "#fff",
                            }}
                          >
                            <Box component="form" onSubmit={handleSubmit} noValidate>



                  <Box id="summary" sx={{ mb: 3 }}>
                    {renderCandidateCardHeader(
                      'Profile summary',
                      'Write a short professional summary',
                      () => openDialog('summary')
                    )}
                    <Paper sx={sectionCardSx}>
                      <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.8 }}>
                        {formData.profileSummary || 'Add a short profile summary to help recruiters understand your background quickly.'}
                      </Typography>
                    </Paper>
                  </Box>

                  <Box id="personal" sx={{ mb: 3 }}>
                    {renderCandidateCardHeader(
                      'Personal details',
                      'Add personal and location details',
                      () => openDialog('personal')
                    )}
                    <Paper sx={sectionCardSx}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Gender</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{personalDetails.gender || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Marital status</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{personalDetails.maritalStatus || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Date of birth</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{personalDetails.dateOfBirth || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Work permit</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{personalDetails.workPermit || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Category</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{personalDetails.category || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Passed Out Year</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{personalDetails.passedOutYear || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Address</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{personalDetails.address || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>

                  <Box id="education" sx={{ mb: 3 }}>
                    {renderCandidateCardHeader(
                      'Education',
                      'Add Your Education Details Here',
                      undefined,
                      <Button
                        variant="text"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => {
                          setEducationDraft({ ...emptyEducation });
                          setEditingEducationIndex(null);
                          openDialog('education');
                        }}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Add education
                      </Button>
                    )}

                    <Paper sx={sectionCardSx}>
                      {educationList.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          No education added yet.
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {educationList.map((edu, index) => (
                            <Box
                              key={edu.id || index}
                              sx={{
                                py: 0.5,
                                borderBottom: index !== educationList.length - 1 ? '1px solid #eef2ff' : 'none'
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                <Box sx={{ flex: 1 }}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>
                                      {edu.education || 'Education'}
                                    </Typography>
                                    <IconButton size="small" onClick={() => editEducation(index)}>
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>

                                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                                    {[edu.course, edu.specialization]
                                      .filter(value => value && value.trim())
                                      .join(', ') || '-'}
                                  </Typography>

                                  <Typography variant="body2" sx={{ color: '#111827', mt: 0.5 }}>
                                    {edu.university || '-'}
                                  </Typography>

                                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                    {edu.startYear || '-'}-{edu.endYear || '-'} | {edu.courseType || '-'}
                                  </Typography>

                                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                    Grading system: {edu.gradingSystem || '-'} {edu.gradingValue ? `| ${edu.gradingValue}` : ''}
                                  </Typography>
                                </Box>

                                <Stack direction="row" spacing={0.5}>
                                  <IconButton onClick={() => editEducation(index)} size="small">
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton onClick={() => deleteEducation(index)} size="small" color="error">
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </Box>

                  <Box id="languages" sx={{ mb: 3 }}>
                    {renderCandidateCardHeader(
                      'Languages',
                      'Add languages and proficiency',
                      () => openDialog('language'),
                      <Button
                        variant="text"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => openDialog('language')}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Add languages
                      </Button>
                    )}
                    <Paper sx={sectionCardSx}>
                      {languages.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          No languages added yet.
                        </Typography>
                      ) : (
                        <Stack spacing={1.5}>
                          {languages.map((lang, index) => (
                            <Box
                              key={lang.id || index}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid #edf7f7',
                                bgcolor: '#ffffff'
                              }}
                            >
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                    {lang.language}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                    Proficiency: {lang.proficiency}
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                    <Chip size="small" label={lang.canRead ? 'Read' : 'No Read'} color={lang.canRead ? 'success' : 'default'} />
                                    <Chip size="small" label={lang.canWrite ? 'Write' : 'No Write'} color={lang.canWrite ? 'success' : 'default'} />
                                    <Chip size="small" label={lang.canSpeak ? 'Speak' : 'No Speak'} color={lang.canSpeak ? 'success' : 'default'} />
                                  </Stack>
                                </Box>

                                <Stack direction="row" spacing={0.5}>
                                  <IconButton size="small" onClick={() => editLanguage(index)}>
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => deleteLanguage(index)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </Box>

                  <Box id="skills" sx={{ mb: 3 }}>
                    {renderCandidateCardHeader(
                      'Skills',
                      'Your key technical skills',
                      () => openDialog('skills')
                    )}
                    <Paper sx={sectionCardSx}>
                      {formData.skills ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {formData.skills.split(',').map((skill) => (
                            <Chip key={skill.trim()} label={skill.trim()} variant="outlined" />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          Add your top skills separated by commas.
                        </Typography>
                      )}
                    </Paper>
                  </Box>

                  <Box id="experience" sx={{ mb: 3 }}>
                    {renderCandidateCardHeader(
                      'Employment',
                      'Fill your Employment Details Here',
                      undefined,
                      <Button
                        variant="text"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => {
                          setExperienceDraft({ ...emptyExperience });
                          setEditingExperienceIndex(null);
                          openDialog('experience');
                        }}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Add employment
                      </Button>
                    )}

                    <Paper sx={sectionCardSx}>
                      {experienceList.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          No employment added yet.
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {experienceList.map((exp, index) => (
                            <Paper
                              key={exp.id || index}
                              sx={{
                                p: 2.4,
                                borderRadius: 3,
                                border: '1px solid #dfe8ff',
                                boxShadow: '0 8px 18px rgba(0,0,0,0.05)',
                                background: '#fff'
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                <Box sx={{ flex: 1 }}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>
                                      {exp.currentJobTitle || 'Job title'}
                                    </Typography>
                                    <IconButton size="small" onClick={() => editExperience(index)}>
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>

                                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                                    {exp.currentCompanyName || 'Company name'}
                                  </Typography>

                                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                    {exp.employmentType || 'Employment type'}{' '}
                                    {exp.joiningYear ? `| Joined ${exp.joiningMonth || ''} ${exp.joiningYear}` : ''}
                                  </Typography>

                                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                    {exp.totalExperienceYears || '0'} Year {exp.totalExperienceMonths || '0'} Months
                                  </Typography>

                                  {exp.noticePeriod && (
                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                      {exp.noticePeriod} Notice Period
                                    </Typography>
                                  )}

                                  {exp.jobProfile && (
                                    <Typography variant="body2" sx={{ mt: 1, color: '#374151', lineHeight: 1.6 }}>
                                      {exp.jobProfile}
                                    </Typography>
                                  )}

                                  {exp.skillsUsed && (
                                    <Typography variant="body2" sx={{ mt: 1, color: '#374151' }}>
                                      <b>Top key skills:</b> {exp.skillsUsed}
                                    </Typography>
                                  )}
                                </Box>

                                <Stack direction="row" spacing={0.5}>
                                  <IconButton onClick={() => editExperience(index)} size="small">
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton onClick={() => deleteExperience(index)} size="small" color="error">
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </Box>

                  <Box id="projects" sx={{ mb: 3 }}>
                    {renderCandidateCardHeader(
                      'Projects',
                      'Add project Details Here',
                      () => openDialog('project'),
                      <Button
                        variant="text"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => openDialog('project')}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Add
                      </Button>
                    )}

                    {projects.length === 0 ? (
                      <Paper sx={sectionCardSx}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          No projects added yet. Click “Add” to create your first project.
                        </Typography>
                      </Paper>
                    ) : (
                      <Stack spacing={2}>
                        {projects.map((project, index) => (
                          <Paper
                            sx={{
                              ...sectionCardSx,
                              p: 2,
                              position: { md: "sticky" },
                              top: { md: 100 },
                              backgroundColor: "#fff",
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>
                                  {project.projectName && project.projectName !== '[]' ? project.projectName : 'Untitled Project'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                  {project.role || 'Role not added'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                  {project.duration || 'Duration not added'}
                                </Typography>

                                {project.techStack && project.techStack !== '[]' && (
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                    {project.techStack.split(',').map((tech) => (
                                      <Chip key={tech.trim()} label={tech.trim()} size="small" variant="outlined" />
                                    ))}
                                  </Stack>
                                )}

                                {project.description && project.description !== '[]' && (
                                  <Typography variant="body2" sx={{ mt: 1.2, color: '#374151', lineHeight: 1.6 }}>
                                    {project.description}
                                  </Typography>
                                )}

                                {project.projectUrl && (
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      size="small"
                                      startIcon={<OpenInNewIcon />}
                                      href={project.projectUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ textTransform: 'none', px: 0 }}
                                    >
                                      View project
                                    </Button>
                                  </Box>
                                )}
                              </Box>

                              <Stack direction="row" spacing={0.5}>
                                <IconButton onClick={() => editProject(index)} size="small">
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton onClick={() => deleteProject(index)} size="small" color="error">
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box id="certifications" sx={{ mb: 3 }}>
                    {renderCandidateCardHeader(
                      'Certification',
                      'Add details of certifications you have completed',
                      undefined,
                      <Button
                        variant="text"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => openDialog('certification')}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Add certification
                      </Button>
                    )}

                    {certifications.length === 0 ? (
                      <Paper sx={sectionCardSx}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          No certifications added yet. Click “Add certification” to create your first certification.
                        </Typography>
                      </Paper>
                    ) : (
                      <Stack spacing={2}>
                        {certifications.map((cert, index) => (
                          <Stack
                            key={cert.id || index}
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                            sx={{
                              py: 1,
                              borderBottom: index !== certifications.length - 1 ? '1px solid #eef2ff' : 'none'
                            }}
                          >
                            <Box
                              sx={{
                                width: 62,
                                height: 62,
                                border: '1px solid #d9e4ff',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                bgcolor: '#fff'
                              }}
                            >
                              <DescriptionOutlined sx={{ fontSize: 36, color: '#d4d8ea' }} />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>
                                  {cert.certificationName && cert.certificationName !== '[]'
                                    ? cert.certificationName
                                    : 'Untitled'}
                                </Typography>

                                <IconButton size="small" onClick={() => editCertification(index)}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => deleteCertification(index)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                              <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.4 }}>
                                {cert.issuedBy || 'Issued by not added'}
                              </Typography>

                              {cert.certificationUrl && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#2f5bea',
                                    mt: 0.4,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {cert.certificationUrl}
                                </Typography>
                              )}

                              {(cert.validityFrom || cert.validityTo || cert.doesNotExpire) && (
                                <Typography variant="body2" sx={{ color: '#8a8fb1', mt: 0.5 }}>
                                  {cert.doesNotExpire
                                    ? 'Validity: Does not expire.'
                                    : `Valid from ${cert.validityFrom || '--'} to ${cert.validityTo || '--'}.`}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box id="links" sx={{ mb: 2 }}>
                    {renderCandidateCardHeader(
                      'Social Links',
                      'Add your professional profiles'
                    )}
                    <Paper sx={sectionCardSx}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="LinkedIn URL" name="linkedinUrl" value={formData.linkedinUrl} onChange={handleChange} sx={inputSx} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="GitHub URL" name="githubUrl" value={formData.githubUrl} onChange={handleChange} sx={inputSx} />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      sx={{
                        minWidth: 180,
                        px: 3,
                        py: 1.2,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 16,
                        boxShadow: '0 10px 20px rgba(30,58,138,0.22)',
                        background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)' }
                      }}
                    >
                      {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>

        <Dialog open={activeDialog === 'basic-info'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>Edit Basic Information</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={basicInfoDraft.firstName}
                  onChange={(e) => setBasicInfoDraft(prev => ({ ...prev, firstName: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={basicInfoDraft.lastName}
                  onChange={(e) => setBasicInfoDraft(prev => ({ ...prev, lastName: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={basicInfoDraft.phone}
                  onChange={(e) => setBasicInfoDraft(prev => ({ ...prev, phone: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              onClick={saveActiveDialog}
              variant="contained"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)'
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'summary'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>Edit Profile Summary</DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Profile Summary"
              value={summaryDraft}
              onChange={(e) => setSummaryDraft(e.target.value)}
              placeholder="Write 3–5 lines about your background, strengths, and career goals."
              sx={inputSx}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button onClick={saveActiveDialog} variant="contained" sx={{ textTransform: 'none', borderRadius: 2, px: 3, background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'personal'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>Edit Personal Details</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  value={personalDraft.gender}
                  onChange={(e) => setPersonalDraft(prev => ({ ...prev, gender: e.target.value }))}
                  sx={inputSx}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Marital Status"
                  value={personalDraft.maritalStatus}
                  onChange={(e) => setPersonalDraft(prev => ({ ...prev, maritalStatus: e.target.value }))}
                  sx={inputSx}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Single">Single</MenuItem>
                  <MenuItem value="Married">Married</MenuItem>
                  <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Birth"
                  value={personalDraft.dateOfBirth}
                  onChange={(e) => setPersonalDraft(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Work Permit"
                  value={personalDraft.workPermit}
                  onChange={(e) => setPersonalDraft(prev => ({ ...prev, workPermit: e.target.value }))}
                  placeholder="India / US / Any"
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category"
                  value={personalDraft.category}
                  onChange={(e) => setPersonalDraft(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="Fresher / Experienced"
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Passed Out Year"
                                value={personalDraft.passedOutYear}
                                onChange={(e) => setPersonalDraft(prev => ({ ...prev, passedOutYear: e.target.value }))}

                                sx={inputSx}
                              />
                            </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Address"
                  value={personalDraft.address}
                  onChange={(e) => setPersonalDraft(prev => ({ ...prev, address: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button onClick={saveActiveDialog} variant="contained" sx={{ textTransform: 'none', borderRadius: 2, px: 3, background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'education'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>
            {editingEducationIndex !== null ? 'Edit Education' : 'Add Education'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Education"
                  value={educationDraft.education}
                  onChange={(e) => setEducationDraft(prev => ({ ...prev, education: e.target.value }))}
                  sx={inputSx}
                >
                  <MenuItem value="Class X">Class X</MenuItem>
                  <MenuItem value="Class XII">Class XII</MenuItem>
                  <MenuItem value="Diploma">Diploma</MenuItem>
                  <MenuItem value="B.Tech / B.E.">B.Tech / B.E.</MenuItem>
                  <MenuItem value="B.Sc">B.Sc</MenuItem>
                  <MenuItem value="M.Tech">M.Tech</MenuItem>
                  <MenuItem value="MCA">MCA</MenuItem>
                  <MenuItem value="MBA">MBA</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="University / Institute"
                  value={educationDraft.university}
                  onChange={(e) => setEducationDraft(prev => ({ ...prev, university: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>



              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Specialization / Branch"
                  value={educationDraft.specialization}
                  onChange={(e) => setEducationDraft(prev => ({ ...prev, specialization: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Course type"
                  value={educationDraft.courseType}
                  onChange={(e) => setEducationDraft(prev => ({ ...prev, courseType: e.target.value }))}
                  sx={inputSx}
                >
                  <MenuItem value="Full time">Full time</MenuItem>
                  <MenuItem value="Part time">Part time</MenuItem>
                  <MenuItem value="Correspondence/Distance learning">Correspondence/Distance learning</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Starting year"
                  value={educationDraft.startYear}
                  onChange={(e) => setEducationDraft(prev => ({ ...prev, startYear: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Ending year"
                  value={educationDraft.endYear}
                  onChange={(e) => setEducationDraft(prev => ({ ...prev, endYear: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Grading system"
                  value={educationDraft.gradingSystem}
                  onChange={(e) => setEducationDraft(prev => ({ ...prev, gradingSystem: e.target.value }))}
                  sx={inputSx}
                >
                  <MenuItem value="Percentage">Percentage</MenuItem>
                  <MenuItem value="CGPA">CGPA</MenuItem>
                  <MenuItem value="Grade">Grade</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="CGPA / Percentage"
                  value={educationDraft.gradingValue}
                  onChange={(e) =>
                    setEducationDraft(prev => ({ ...prev, gradingValue: e.target.value }))
                  }
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              onClick={() => {
                if (!educationDraft.education.trim() || !educationDraft.university.trim()) {
                  setError('Education and University/Institute are required');
                  return;
                }

                setEducationList(prev => {
                  const next = [...prev];
                  const item = {
                    id: editingEducationIndex !== null ? next[editingEducationIndex]?.id : Date.now().toString(),
                    ...educationDraft
                  };

                  if (editingEducationIndex !== null) {
                    next[editingEducationIndex] = item;
                  } else {
                    next.push(item);
                  }
                  return next;
                });

                closeDialog();
              }}
              variant="contained"
              sx={{ textTransform: 'none', borderRadius: 2, px: 3, background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)' }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'skills'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>Edit Skills</DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Skills"
              value={skillsDraft}
              onChange={(e) => setSkillsDraft(e.target.value)}
              placeholder="Enter your skills"
              sx={inputSx}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button onClick={saveActiveDialog} variant="contained" sx={{ textTransform: 'none', borderRadius: 2, px: 3, background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'experience'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>
            {editingExperienceIndex !== null ? 'Edit Employment' : 'Add Employment'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Current employment?"
                  value={experienceDraft.currentEmployment}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, currentEmployment: e.target.value }))}
                  sx={inputSx}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Employment type"
                  value={experienceDraft.employmentType}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, employmentType: e.target.value }))}
                  sx={inputSx}
                >
                  <MenuItem value="Full-time">Full-time</MenuItem>
                  <MenuItem value="Part-time">Part-time</MenuItem>
                  <MenuItem value="Internship">Internship</MenuItem>
                  <MenuItem value="Contract">Contract</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Total experience years"
                  value={experienceDraft.totalExperienceYears}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, totalExperienceYears: e.target.value }))}
                  sx={inputSx}
                >
                  {Array.from({ length: 31 }, (_, i) => (
                    <MenuItem key={i} value={String(i)}>{i} Year</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Total experience months"
                  value={experienceDraft.totalExperienceMonths}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, totalExperienceMonths: e.target.value }))}
                  sx={inputSx}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i} value={String(i)}>{i} Month</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current company name"
                  value={experienceDraft.currentCompanyName}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, currentCompanyName: e.target.value }))}
                  placeholder="Type your organization"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current job title"
                  value={experienceDraft.currentJobTitle}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, currentJobTitle: e.target.value }))}
                  placeholder="Job title"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Joining year"
                  value={experienceDraft.joiningYear}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, joiningYear: e.target.value }))}
                  sx={inputSx}
                >
                  {Array.from({ length: 25 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return <MenuItem key={year} value={String(year)}>{year}</MenuItem>;
                  })}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Joining month"
                  value={experienceDraft.joiningMonth}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, joiningMonth: e.target.value }))}
                  sx={inputSx}
                >
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={experienceDraft.currentSalaryCurrency}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, currentSalaryCurrency: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={10}>
                <TextField
                  fullWidth
                  label="Current salary"
                  value={experienceDraft.currentSalary}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, currentSalary: e.target.value }))}
                  placeholder="2"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Skills used"
                  value={experienceDraft.skillsUsed}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, skillsUsed: e.target.value }))}
                  placeholder="Enter skills used (e.g. Skill 1, Skill 2...)"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label="Job profile"
                  value={experienceDraft.jobProfile}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, jobProfile: e.target.value }))}
                  placeholder="Write your role, responsibilities, and achievements..."
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notice period"
                  value={experienceDraft.noticePeriod}
                  onChange={(e) => setExperienceDraft(prev => ({ ...prev, noticePeriod: e.target.value }))}
                  placeholder="15 Days or less"
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              onClick={() => {
                setExperienceList(prev => {
                  const next = [...prev];
                  const item = {
                    id: editingExperienceIndex !== null ? next[editingExperienceIndex]?.id : Date.now().toString(),
                    ...experienceDraft
                  };

                  if (editingExperienceIndex !== null) {
                    next[editingExperienceIndex] = item;
                  } else {
                    next.push(item);
                  }
                  return next;
                });

                closeDialog();
              }}
              variant="contained"
              sx={{ textTransform: 'none', borderRadius: 2, px: 3, background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)' }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'resume'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>Upload Resume</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px dashed #cfdede',
                    bgcolor: '#fbffff',
                    textAlign: 'center'
                  }}
                >
                  <Button variant="outlined" component="label" startIcon={<DescriptionOutlined />} sx={{ textTransform: 'none', borderRadius: 2 }}>
                    Choose file
                    <input hidden type="file" accept=".pdf,.doc,.docx,.rtf" onChange={handleResumeFileChange} />
                  </Button>
                  <Typography variant="body2" sx={{ mt: 1.2, color: '#6b7280' }}>
                    Supported formats: doc, docx, rtf, pdf, up to 2 MB
                  </Typography>
                  {resumeDraft.resumeFileName && (
                    <Chip sx={{ mt: 2, fontWeight: 700 }} label={resumeDraft.resumeFileName} />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Resume URL / Preview URL"
                  value={resumeDraft.resumeUrl}
                  onChange={(e) => setResumeDraft(prev => ({ ...prev, resumeUrl: e.target.value }))}
                  placeholder="https://..."
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button onClick={saveActiveDialog} variant="contained" sx={{ textTransform: 'none', borderRadius: 2, px: 3, background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'language'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>
            {editingLanguageIndex !== null ? 'Edit Language' : 'Add Language'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Language"
                  value={languageDraft.language}
                  onChange={(e) => setLanguageDraft(prev => ({ ...prev, language: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Proficiency"
                  value={languageDraft.proficiency}
                  onChange={(e) => setLanguageDraft(prev => ({ ...prev, proficiency: e.target.value }))}
                  sx={inputSx}
                >
                  <MenuItem value="Beginner">Beginner</MenuItem>
                  <MenuItem value="Intermediate">Intermediate</MenuItem>
                  <MenuItem value="Proficient">Proficient</MenuItem>
                  <MenuItem value="Fluent">Fluent</MenuItem>
                  <MenuItem value="Native">Native</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={languageDraft.canRead}
                        onChange={(e) => setLanguageDraft(prev => ({ ...prev, canRead: e.target.checked }))}
                      />
                    }
                    label="Read"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={languageDraft.canWrite}
                        onChange={(e) => setLanguageDraft(prev => ({ ...prev, canWrite: e.target.checked }))}
                      />
                    }
                    label="Write"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={languageDraft.canSpeak}
                        onChange={(e) => setLanguageDraft(prev => ({ ...prev, canSpeak: e.target.checked }))}
                      />
                    }
                    label="Speak"
                  />
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button onClick={saveActiveDialog} variant="contained" sx={{ textTransform: 'none', borderRadius: 2, px: 3, background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'project'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>
            {editingProjectIndex !== null ? 'Edit Project' : 'Add Project'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Project Name *" value={projectDraft.projectName} onChange={(e) => setProjectDraft(prev => ({ ...prev, projectName: e.target.value }))} sx={inputSx} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Tech Stack" value={projectDraft.techStack} onChange={(e) => setProjectDraft(prev => ({ ...prev, techStack: e.target.value }))} placeholder="React, Node.js, PostgreSQL" sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Duration" value={projectDraft.duration} onChange={(e) => setProjectDraft(prev => ({ ...prev, duration: e.target.value }))} placeholder="Jan 2025 - Mar 2025" sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Project URL / GitHub URL" value={projectDraft.projectUrl} onChange={(e) => setProjectDraft(prev => ({ ...prev, projectUrl: e.target.value }))} sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={4} label="Project Description" value={projectDraft.description} onChange={(e) => setProjectDraft(prev => ({ ...prev, description: e.target.value }))} sx={inputSx} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button onClick={saveActiveDialog} variant="contained" sx={{ textTransform: 'none', borderRadius: 2, px: 3, background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'certification'} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800 }}>
            {editingCertIndex !== null ? 'Edit Certification' : 'Add Certification'}
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Certification name *"
                  value={certDraft.certificationName}
                  onChange={(e) =>
                    setCertDraft(prev => ({ ...prev, certificationName: e.target.value }))
                  }
                  placeholder="AWS Certified Developer"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Issued by / Organization"
                  value={certDraft.issuedBy}
                  onChange={(e) =>
                    setCertDraft(prev => ({ ...prev, issuedBy: e.target.value }))
                  }
                  placeholder="Amazon Web Services"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Certification ID"
                  value={certDraft.certificationId}
                  onChange={(e) =>
                    setCertDraft(prev => ({ ...prev, certificationId: e.target.value }))
                  }
                  placeholder="Completion / credential id"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Certification URL"
                  value={certDraft.certificationUrl}
                  onChange={(e) =>
                    setCertDraft(prev => ({ ...prev, certificationUrl: e.target.value }))
                  }
                  placeholder="https://..."
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Validity From (YYYY-MM)"
                  value={certDraft.validityFrom}
                  onChange={(e) =>
                    setCertDraft(prev => ({ ...prev, validityFrom: e.target.value }))
                  }
                  placeholder="2026-05"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Validity To (YYYY-MM)"
                  value={certDraft.validityTo}
                  onChange={(e) =>
                    setCertDraft(prev => ({ ...prev, validityTo: e.target.value }))
                  }
                  placeholder="2027-05"
                  disabled={certDraft.doesNotExpire}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={certDraft.doesNotExpire}
                      onChange={(e) =>
                        setCertDraft(prev => ({ ...prev, doesNotExpire: e.target.checked }))
                      }
                    />
                  }
                  label="This certification does not expire"
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={saveActiveDialog}
              variant="contained"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                background: 'linear-gradient(135deg, #1E3A8A 0%, #0f766e 100%)'
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default Profile;
